import * as React from 'react';
import { t } from '../../services/HelperService';
import { OUTPUTS } from './supplyOrderIntegrationDefinition';
import { stripSerializerMetadata } from '../workflow/tracking/workflowCardRuntime';

const safeString = (v) => (v ?? '').toString();

const panelStyle = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, minHeight: 0, flex: 1 };
const fillStyle = { display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0, flex: 1 };
const mutedStyle = { color: '#6c757d', fontSize: 12.5 };
const cardStyle = { border: '1px solid #dee2e6', borderRadius: 4, padding: '6px 8px', background: '#fff', fontSize: 12.5 };
const errorStyle = { color: '#b02a37', fontSize: 12.5, whiteSpace: 'pre-wrap', background: '#fff5f5', borderRadius: 4, padding: '6px 8px' };
const warnStyle = { color: '#7a5a00', fontSize: 12.5, whiteSpace: 'pre-wrap', background: '#fff9e6', borderRadius: 4, padding: '6px 8px' };
const preStyle = { margin: 0, fontFamily: 'Consolas, Menlo, monospace', fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflow: 'auto', background: '#f8f9fa', borderRadius: 4, padding: 6 };

const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const pick = (o, ...keys) => {
  for (const k of keys) if (o?.[k] !== undefined) return o[k];
  return undefined;
};

/** Output-by-output outcome strip: which of the five scripts ran clean against this record. */
export const OutputStatusStrip = ({ errors }) => {
  const map = errors && typeof errors === 'object' ? errors : {};
  const lookup = (name) => {
    const key = Object.keys(map).find((k) => k.toLowerCase() === name.toLowerCase());
    return key === undefined ? null : (Array.isArray(map[key]) ? map[key] : []);
  };
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
      {OUTPUTS.map((name) => {
        const list = lookup(name);
        const state = list === null ? 'absent' : (list.length === 0 ? 'ok' : 'failed');
        const color = state === 'ok' ? '#198754' : (state === 'failed' ? '#b02a37' : '#6c757d');
        const glyph = state === 'ok' ? '\u2713' : (state === 'failed' ? '\u2717' : '\u2014');
        return (
          <span key={name} style={{ color }} title={list?.join('\n') || (state === 'absent' ? t('Not written yet') : t('Ran clean'))}>
            {glyph} {name}
          </span>
        );
      })}
    </div>
  );
};

/**
 * A row of tabs in the preview's own style (the Run / Mapping / Products strip above the panels uses the same look).
 * `items`: [{ key, label, tag?, badge?, tone? ('error' colours the label) }]; hidden when there is one item or none.
 */
export const PreviewTabStrip = ({ items, active, onSelect, tag, small }) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length <= 1) return null;
  return (
    <div role="tablist" data-tagname={tag} style={{ display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0' }}>
      {list.map((x) => {
        const selected = x.key === active;
        const color = x.tone === 'error' ? '#b02a37' : (selected ? '#4c2a85' : '#475569');
        return (
          <button key={x.key} type="button" role="tab" aria-selected={selected} data-tagname={x.tag} onClick={() => onSelect(x.key)}
            style={{
              border: 'none', borderBottom: selected ? '2px solid #6f42c1' : '2px solid transparent', background: 'none', cursor: 'pointer',
              fontSize: small ? 12 : 12.5, fontWeight: selected ? 700 : 500, color, padding: small ? '3px 8px' : '4px 10px', marginBottom: -1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {x.label}
            {x.badge}
          </button>
        );
      })}
    </div>
  );
};

/** The index of `active` in `list`, clamped so a re-run that changes the count never points past the end. */
const clampIndex = (active, list) => Math.min(Math.max(active, 0), Math.max(list.length - 1, 0));

const gridWrapStyle = { flex: 1, minHeight: 0, overflow: 'auto', background: '#fff', border: '1px solid #dee2e6', borderRadius: 4 };
/** The widest a cell is let grow (characters are ~7px in the grid's font); a 40-column sheet scrolls sideways either way. */
const cellMaxWidth = 480;
const gridTableStyle = { borderCollapse: 'collapse', fontSize: 11.5, fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif', tableLayout: 'auto' };
const gridHeadStyle = { background: '#f1f3f5', color: '#6c757d', fontWeight: 400, textAlign: 'center', border: '1px solid #dee2e6', padding: '0 4px', position: 'sticky', top: 0, zIndex: 1, minWidth: 28 };
const gridRowHeadStyle = { ...gridHeadStyle, position: 'sticky', left: 0, top: 'auto', textAlign: 'right', minWidth: 28 };

/** 1-based column index -> spreadsheet letters (1 -> A, 27 -> AA). */
const columnLetters = (index) => {
  let n = index; let s = '';
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
};

/**
 * One rendered worksheet of a workbook file: a read-only grid that mirrors what the xlsx holds - the cell text as the
 * sheet shows it, bold / italic / colours / alignment / borders / merges, frozen panes marked, column widths honoured.
 * `sheet` is the server's { name, rowCount, columnCount, truncated, freezeRows, freezeColumns, columnWidths, rows[][cell] }.
 */
export const WorkbookSheetGrid = ({ sheet }) => {
  const rows = Array.isArray(pick(sheet, 'rows', 'Rows')) ? pick(sheet, 'rows', 'Rows') : [];
  const columnCount = Math.max(Number(pick(sheet, 'columnCount', 'ColumnCount')) || 0, ...rows.map((r) => (Array.isArray(r) ? r.length : 0)), 1);
  const freezeRows = Number(pick(sheet, 'freezeRows', 'FreezeRows')) || 0;
  const freezeColumns = Number(pick(sheet, 'freezeColumns', 'FreezeColumns')) || 0;
  const widths = pick(sheet, 'columnWidths', 'ColumnWidths') ?? {};
  const truncated = pick(sheet, 'truncated', 'Truncated') === true;
  // merged cells: a cell with colSpan / rowSpan hides the cells it covers
  const covered = new Set();
  rows.forEach((r, ri) => (Array.isArray(r) ? r : []).forEach((c, ci) => {
    const cs = Number(pick(c ?? {}, 'colSpan', 'ColSpan')) || 1;
    const rs = Number(pick(c ?? {}, 'rowSpan', 'RowSpan')) || 1;
    if (cs <= 1 && rs <= 1) return;
    for (let dr = 0; dr < rs; dr++) for (let dc = 0; dc < cs; dc++) if (dr > 0 || dc > 0) covered.add(`${ri + dr}:${ci + dc}`);
  }));
  const cellStyle = (c, ri, ci) => {
    const kind = safeString(pick(c, 'kind', 'Kind'));
    const align = pick(c, 'align', 'Align') || (kind === 'number' || kind === 'date' || kind === 'datetime' ? 'right' : (kind === 'bool' ? 'center' : 'left'));
    const style = {
      border: pick(c, 'border', 'Border') === true ? '1px solid #495057' : '1px solid #e9ecef',
      padding: '1px 4px',
      whiteSpace: pick(c, 'wrap', 'Wrap') === true ? 'pre-wrap' : 'nowrap',
      textAlign: align,
      verticalAlign: 'bottom',
      fontWeight: pick(c, 'bold', 'Bold') === true ? 700 : 400,
      fontStyle: pick(c, 'italic', 'Italic') === true ? 'italic' : 'normal',
      color: pick(c, 'color', 'Color') || undefined,
      background: pick(c, 'background', 'Background') || undefined,
      maxWidth: cellMaxWidth,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
    if (freezeRows > 0 && ri === freezeRows - 1) style.borderBottom = '2px solid #868e96';
    if (freezeColumns > 0 && ci === freezeColumns - 1) style.borderRight = '2px solid #868e96';
    const width = widths?.[String(ci + 1)];
    if (width > 0) style.minWidth = Math.min(Math.round(width * 7), cellMaxWidth);
    return style;
  };
  return (
    <div style={fillStyle}>
      <div style={gridWrapStyle}>
        <table style={gridTableStyle}>
          <thead>
            <tr>
              <th style={gridRowHeadStyle} />
              {Array.from({ length: columnCount }, (_, ci) => <th key={ci} style={gridHeadStyle}>{columnLetters(ci + 1)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                <th style={gridRowHeadStyle}>{ri + 1}</th>
                {Array.from({ length: columnCount }, (_, ci) => {
                  if (covered.has(`${ri}:${ci}`)) return null;
                  const c = Array.isArray(r) ? r[ci] : null;
                  if (!c) return <td key={ci} style={{ border: '1px solid #e9ecef', padding: '1px 4px' }} />;
                  const cs = Number(pick(c, 'colSpan', 'ColSpan')) || 1;
                  const rs = Number(pick(c, 'rowSpan', 'RowSpan')) || 1;
                  return (
                    <td key={ci} colSpan={cs > 1 ? cs : undefined} rowSpan={rs > 1 ? rs : undefined} style={cellStyle(c, ri, ci)} title={safeString(pick(c, 'comment', 'Comment')) || undefined}>
                      {safeString(pick(c, 'text', 'Text'))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...mutedStyle, fontSize: 11, flex: 'none' }}>
        {t('{{rows}} row(s) x {{cols}} column(s)', { rows: pick(sheet, 'rowCount', 'RowCount') ?? rows.length, cols: pick(sheet, 'columnCount', 'ColumnCount') ?? columnCount })}
        {truncated ? ` \u00b7 ${t('preview cut; download the file for the rest')}` : ''}
        {freezeRows > 0 || freezeColumns > 0 ? ` \u00b7 ${t('frozen: {{rows}} row(s), {{cols}} column(s)', { rows: freezeRows, cols: freezeColumns })}` : ''}
      </div>
    </div>
  );
};

/** The sheets of a workbook file with a tab per sheet (one sheet shows without tabs); the grid fills the height it is given. */
export const WorkbookPreview = ({ sheets }) => {
  const list = Array.isArray(sheets) ? sheets : [];
  const [active, setActive] = React.useState(0);
  const index = clampIndex(active, list);
  if (list.length === 0) return <div style={mutedStyle}>{t('The workbook has no sheets.')}</div>;
  return (
    <div style={fillStyle}>
      <PreviewTabStrip
        tag="supplyOrderPreviewSheetTabs" small
        items={list.map((s, i) => ({ key: i, label: safeString(pick(s, 'name', 'Name')) || t('Sheet {{n}}', { n: i + 1 }), tag: `supplyOrderPreviewSheetTab-${i}` }))}
        active={index} onSelect={setActive}
      />
      <WorkbookSheetGrid sheet={list[index]} />
    </div>
  );
};

// a component, not a constant: `t` is bound by HelperService, which this module reaches through an import cycle, so it
// must not be called while the module loads
const PrimaryBadge = () => <span style={{ ...mutedStyle, border: '1px solid #ced4da', borderRadius: 3, padding: '0 4px', fontSize: 11 }}>{t('primary')}</span>;

/**
 * Panel 1: the files the dispatch produced - a tab per file when there are several (one file shows without tabs), the
 * active file's route and details once above its content, a workbook's sheets as a grid (with sheet tabs when it has
 * several) that fills the panel; `onDownload(file)` adds a Download link.
 */
export const ProducedFilesPanel = ({ files, errors, onDownload }) => {
  const list = Array.isArray(files) ? files : [];
  const dispatchErrors = pick(errors ?? {}, 'dispatch', 'Dispatch') ?? [];
  const [active, setActive] = React.useState(0);
  const index = clampIndex(active, list);
  const f = list[index];
  const name = safeString(pick(f ?? {}, 'name', 'Name'));
  const primary = pick(f ?? {}, 'primary', 'Primary') === true;
  const route = f ? [pick(f, 'transport', 'Transport'), pick(f, 'url', 'Url'), pick(f, 'path', 'Path')].filter(Boolean).join(' \u00b7 ') : '';
  const preview = pick(f ?? {}, 'preview', 'Preview');
  const json = pick(f ?? {}, 'json', 'Json');
  const sheets = pick(f ?? {}, 'sheets', 'Sheets');
  const isWorkbook = Array.isArray(sheets);
  const encoding = pick(f ?? {}, 'encoding', 'Encoding');
  const encodingText = isWorkbook ? `${t('workbook')} (${safeString(pick(f, 'output', 'Output')) || 'xlsx'}${pick(f, 'sheetScriptApiVersion', 'SheetScriptApiVersion') ? `, S ${pick(f, 'sheetScriptApiVersion', 'SheetScriptApiVersion')}` : ''})` : encoding;
  return (
    <div style={panelStyle} data-tagname="supplyOrderPreviewFilesPanel">
      {dispatchErrors.length > 0 && <div style={errorStyle}>{dispatchErrors.join('\n')}</div>}
      {list.length === 0 && dispatchErrors.length === 0 && <div style={mutedStyle}>{t('No files yet - the dispatch script produces them.')}</div>}
      <PreviewTabStrip
        tag="supplyOrderPreviewFileTabs" small
        items={list.map((x, i) => ({ key: i, label: safeString(pick(x, 'name', 'Name')) || t('(unnamed file)'), badge: pick(x, 'primary', 'Primary') === true ? <PrimaryBadge /> : null, tag: `supplyOrderPreviewFileTab-${i}` }))}
        active={index} onSelect={setActive}
      />
      {f && (
        <div style={{ ...cardStyle, ...fillStyle, gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 'none' }}>
            <strong>{name || t('(unnamed file)')}</strong>
            {primary && <PrimaryBadge />}
            <span style={mutedStyle}>{[safeString(pick(f, 'mimeType', 'MimeType')), formatSize(pick(f, 'size', 'Size')), encodingText, pick(f, 'postAs', 'PostAs')].filter(Boolean).join(' \u00b7 ')}</span>
            {route && <span style={mutedStyle}>{route}</span>}
            {onDownload && name && (
              <a href="#download" style={{ marginLeft: 'auto', fontSize: 12 }} onClick={(e) => { e.preventDefault(); onDownload(f); }}>{t('Download')}</a>
            )}
          </div>
          {isWorkbook && <WorkbookPreview key={name || index} sheets={sheets} />}
          {!isWorkbook && (preview || json) && (
            <pre style={{ ...preStyle, maxHeight: 'none', flex: 1, minHeight: 0 }}>{json !== undefined && json !== null ? JSON.stringify(json, null, 2) : safeString(preview)}</pre>
          )}
        </div>
      )}
    </div>
  );
};

/** Panel 2: the send plan - one row per transport the files route to, in send order, with masked headers. */
export const SendPlanPanel = ({ rows, routeConflict, errors }) => {
  const list = Array.isArray(rows) ? rows : null;
  const transportErrors = pick(errors ?? {}, 'transports', 'Transports') ?? [];
  const credentialErrors = pick(errors ?? {}, 'credentials', 'Credentials') ?? [];
  return (
    <div style={panelStyle} data-tagname="supplyOrderPreviewSendPlanPanel">
      {transportErrors.length > 0 && <div style={errorStyle}>{transportErrors.join('\n')}</div>}
      {credentialErrors.length > 0 && <div style={errorStyle}>{credentialErrors.join('\n')}</div>}
      {routeConflict && <div style={errorStyle}>{routeConflict}</div>}
      {!list && transportErrors.length === 0 && <div style={mutedStyle}>{t('The plan appears once the transports and the dispatch both run clean.')}</div>}
      {list && list.length === 0 && <div style={mutedStyle}>{t('No file routes to a declared transport.')}</div>}
      {(list ?? []).map((r, i) => {
        const type = safeString(pick(r, 'type', 'Type'));
        const files = pick(r, 'files', 'Files') ?? [];
        const standard = pick(r, 'standardFiles', 'StandardFiles') ?? [];
        // the web app's serializer adds "$id" / "$type" members to every dictionary; they are not request headers
        const headers = stripSerializerMetadata(pick(r, 'headers', 'Headers'));
        const warnings = pick(r, 'warnings', 'Warnings') ?? [];
        const headerLines = Object.keys(headers).map((k) => `${k}: ${headers[k]}`);
        return (
          <div key={`${pick(r, 'transport', 'Transport')}-${i}`} style={cardStyle}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ ...mutedStyle, minWidth: 18 }}>{pick(r, 'order', 'Order') ?? i + 1}.</span>
              <strong>{safeString(pick(r, 'transport', 'Transport'))}</strong>
              <span style={mutedStyle}>{type}</span>
              {pick(r, 'carriesResponse', 'CarriesResponse') === true && <span style={{ ...mutedStyle, border: '1px solid #ced4da', borderRadius: 3, padding: '0 4px' }}>{t('reply read')}</span>}
            </div>
            {type === 'webEndpoint' && (
              <div style={mutedStyle}>{safeString(pick(r, 'method', 'Method')) || 'POST'} {safeString(pick(r, 'url', 'Url'))}{pick(r, 'postAs', 'PostAs') ? ` (${pick(r, 'postAs', 'PostAs')})` : ''}</div>
            )}
            {type === 'sftp' && (
              <div style={mutedStyle}>{safeString(pick(r, 'host', 'Host'))}{pick(r, 'port', 'Port') ? `:${pick(r, 'port', 'Port')}` : ''} {(pick(r, 'remotePaths', 'RemotePaths') ?? []).join(', ')}</div>
            )}
            {type === 'email' && (
              <div style={mutedStyle}>{t('To')}: {safeString(pick(r, 'to', 'To'))}{pick(r, 'subject', 'Subject') ? ` \u00b7 ${pick(r, 'subject', 'Subject')}` : ''}</div>
            )}
            <div style={mutedStyle}>{t('Files')}: {[...files, ...standard].join(', ') || t('(none)')}</div>
            {headerLines.length > 0 && <pre style={{ ...preStyle, maxHeight: 120 }}>{headerLines.join('\n')}</pre>}
            {warnings.length > 0 && <div style={warnStyle}>{warnings.join('\n')}</div>}
          </div>
        );
      })}
    </div>
  );
};

/** Panel 3: the orderResponse script replayed over every recorded sample reply. */
export const SampleRepliesPanel = ({ samples, errors }) => {
  const list = Array.isArray(samples) ? samples : [];
  const responseErrors = pick(errors ?? {}, 'orderResponse', 'OrderResponse') ?? [];
  return (
    <div style={panelStyle} data-tagname="supplyOrderPreviewRepliesPanel">
      {list.length === 0 && responseErrors.length > 0 && <div style={errorStyle}>{responseErrors.join('\n')}</div>}
      {list.length === 0 && responseErrors.length === 0 && <div style={mutedStyle}>{t('No sample replies recorded. Paste the supplier\'s documented reply into the assistant, or save a captured reply from the send log.')}</div>}
      {list.map((s, i) => {
        const error = pick(s, 'error', 'Error');
        const accepted = pick(s, 'accepted', 'Accepted');
        const messages = pick(s, 'messages', 'Messages') ?? [];
        const warnings = pick(s, 'warnings', 'Warnings') ?? [];
        const color = error ? '#b02a37' : (accepted === true ? '#198754' : (accepted === false ? '#7a5a00' : '#6c757d'));
        return (
          <div key={`${pick(s, 'name', 'Name')}-${i}`} style={cardStyle}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{safeString(pick(s, 'name', 'Name'))}</strong>
              <span style={mutedStyle}>{safeString(pick(s, 'source', 'Source'))}{pick(s, 'status', 'Status') ? ` \u00b7 HTTP ${pick(s, 'status', 'Status')}` : ''}</span>
              <span style={{ color, marginLeft: 'auto' }}>
                {error ? t('script failed') : (accepted === true ? t('accepted') : (accepted === false ? t('rejected') : t('no verdict')))}
              </span>
            </div>
            {!error && pick(s, 'supplierOrderNumber', 'SupplierOrderNumber') && (
              <div style={mutedStyle}>{t('Supplier order number')}: {safeString(pick(s, 'supplierOrderNumber', 'SupplierOrderNumber'))}</div>
            )}
            {!error && messages.length > 0 && <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{messages.join('\n')}</div>}
            {error && <div style={errorStyle}>{safeString(error)}</div>}
            {warnings.length > 0 && <div style={warnStyle}>{warnings.join('\n')}</div>}
          </div>
        );
      })}
    </div>
  );
};

const errorsOf = (errors, ...names) => names.flatMap((n) => pick(errors ?? {}, n, n[0].toUpperCase() + n.slice(1)) ?? []);

/**
 * The three preview panels of a supplyOrderIntegration customisation - files produced / send plan / sample replies - as
 * tabs, so the files' sheets get the whole pane, from a SupplyOrderIntegration_Preview result. A tab whose output failed
 * is coloured. Fills the height its host gives it (the host is a flex column with overflow hidden). Shared by the
 * editor's Preview and the assistant's Preview tab so both show exactly what the runtime produced for the test supply job.
 */
export const SupplyOrderIntegrationPreviewPanels = ({ result, onDownload }) => {
  const [tab, setTab] = React.useState('files');
  if (!result) return null;
  const errors = pick(result, 'Errors', 'errors') ?? {};
  const warnings = pick(result, 'Warnings', 'warnings') ?? [];
  // the dataStore output has no panel of its own: its failures (a malformed request, a read the script did not
  // request, a required row the store does not hold) are shown here, above the panels they stop
  const dataStoreErrors = pick(errors, 'dataStore', 'DataStore') ?? [];
  const dataStoreRows = pick(result, 'DataStoreRows', 'dataStoreRows');
  // Missing: an optional row the scripts read as undefined; Pending: a required row the operator has not entered yet (this
  // run read its sample; the send refuses until it is set) - both named here, neither fails the preview
  const missingRows = Array.isArray(dataStoreRows) ? dataStoreRows.filter((r) => ['Missing', 'Pending'].includes(pick(r, 'Status', 'status'))) : [];
  const files = pick(result, 'Files', 'files');
  const sendPlan = pick(result, 'SendPlan', 'sendPlan');
  const samples = pick(result, 'Samples', 'samples');
  const count = (list) => (Array.isArray(list) ? ` (${list.length})` : '');
  const tabs = [
    { key: 'files', label: `${t('Files produced')}${count(files)}`, tag: 'supplyOrderPreviewFilesTab', tone: errorsOf(errors, 'dispatch').length > 0 ? 'error' : undefined },
    { key: 'sendPlan', label: `${t('Send plan')}${count(sendPlan)}`, tag: 'supplyOrderPreviewSendPlanTab', tone: errorsOf(errors, 'transports', 'credentials').length > 0 || pick(result, 'RouteConflict', 'routeConflict') ? 'error' : undefined },
    { key: 'replies', label: `${t('Sample replies')}${count(samples)}`, tag: 'supplyOrderPreviewRepliesTab', tone: errorsOf(errors, 'orderResponse').length > 0 ? 'error' : undefined },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, height: '100%' }}>
      <OutputStatusStrip errors={errors} />
      {dataStoreErrors.length > 0 && <div style={errorStyle}>{dataStoreErrors.join('\n')}</div>}
      {missingRows.length > 0 && (
        <div style={warnStyle}>
          {t('Data store rows not set: {{rows}}', { rows: missingRows.map((r) => pick(r, 'Reference', 'reference')).join(', ') })}
        </div>
      )}
      {warnings.length > 0 && <div style={warnStyle}>{warnings.join('\n')}</div>}
      <PreviewTabStrip tag="supplyOrderPreviewPanelTabs" items={tabs} active={tab} onSelect={setTab} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: tab === 'files' ? 'hidden' : 'auto' }}>
        {tab === 'files' && <ProducedFilesPanel files={files} errors={errors} onDownload={onDownload} />}
        {tab === 'sendPlan' && <SendPlanPanel rows={sendPlan} routeConflict={pick(result, 'RouteConflict', 'routeConflict')} errors={errors} />}
        {tab === 'replies' && <SampleRepliesPanel samples={samples} errors={errors} />}
      </div>
    </div>
  );
};

export default SupplyOrderIntegrationPreviewPanels;
